import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { trackWhatsAppClick, trackEventCustom } from "../../utils/analytics";

const SITE_URL = "https://hexxatech.co.in";
const PAGE_URL = `${SITE_URL}/services/erp-business-management`;

const features = [
  {
    title: "Purchase Management",
    text: "Manage purchase orders, vendors, purchase transactions and procurement workflows from a centralized system.",
  },
  {
    title: "Sales Management",
    text: "Manage customers, quotations, sales orders, invoices and sales transactions through a connected workflow.",
  },
  {
    title: "Inventory Management",
    text: "Track stock availability, inward and outward movements, warehouses, materials and inventory transactions.",
  },
  {
    title: "Quotation Management",
    text: "Create, manage and track quotations with structured approval, revision and conversion workflows.",
  },
  {
    title: "Business Dashboards",
    text: "Get centralized dashboards and reports for sales, purchases, inventory, customers and operational performance.",
  },
  {
    title: "Approval Workflows",
    text: "Digitize internal approvals and business processes to reduce manual coordination and improve accountability.",
  },
  {
    title: "Customer & Vendor Management",
    text: "Maintain centralized customer and vendor information with transaction history and operational records.",
  },
  {
    title: "Reports & Analytics",
    text: "Generate business reports that help management understand operations, financial activity and business performance.",
  },
];

const modules = [
  "Purchase Management",
  "Sales Management",
  "Inventory Management",
  "Quotation Management",
  "Customer Management",
  "Vendor Management",
  "Order Management",
  "Invoice Management",
  "Payment Tracking",
  "Business Dashboards",
  "Reports & Analytics",
  "Approval Workflows",
];

const benefits = [
  {
    title: "Centralized Business Data",
    text: "Bring important business information into one connected platform instead of managing multiple disconnected systems.",
  },
  {
    title: "Reduced Manual Work",
    text: "Automate repetitive operational activities and reduce dependency on spreadsheets and manual data entry.",
  },
  {
    title: "Better Visibility",
    text: "Give management and teams access to relevant business information through dashboards and reports.",
  },
  {
    title: "Process Control",
    text: "Define structured workflows, approvals and business rules around the way your organization operates.",
  },
  {
    title: "Scalable Architecture",
    text: "Build an ERP platform that can evolve as your business grows and operational requirements change.",
  },
  {
    title: "Custom Business Logic",
    text: "Implement business-specific rules, calculations, workflows and integrations that generic ERP systems may not support.",
  },
];

const industries = [
  "Manufacturing",
  "Trading",
  "Distribution",
  "Automotive",
  "Healthcare",
  "Engineering",
  "Service Businesses",
  "SMEs",
];

const processSteps = [
  {
    number: "01",
    title: "Understand Your Business",
    text: "We study your departments, users, transactions, approvals, reports and existing business processes.",
  },
  {
    number: "02",
    title: "Define ERP Modules",
    text: "We identify the modules and workflows required to create a practical ERP system for your organization.",
  },
  {
    number: "03",
    title: "Design & Development",
    text: "Our team designs the interface, database and application architecture before developing the system module-by-module.",
  },
  {
    number: "04",
    title: "Testing & Deployment",
    text: "The application is tested against real business scenarios before deployment and user adoption.",
  },
  {
    number: "05",
    title: "Support & Improvements",
    text: "We continue to provide technical support, maintenance and enhancements as your business requirements evolve.",
  },
];

const caseStudies = [
  {
    title: "Inventory Management",
    category: "Business Operations",
    description:
      "Custom ERP and inventory software covering purchasing, sales, vendors, stock tracking and operational reporting.",
    link: "/case-studies/inventory-management",
  },
  {
    title: "Process Control System",
    category: "Manufacturing",
    description:
      "Industrial business software connecting quotation, orders, inward, BOM, process vouchers, FGI, invoicing and payment workflows.",
    link: "/case-studies/process-control",
  },
  {
    title: "CarTattva",
    category: "Automotive",
    description:
      "A business management platform connecting customers, dealers, leads, appointments, inspections, reports and operational workflows.",
    link: "/case-studies/cartattva",
  },
];

export default function ERPBusinessManagement() {
  useEffect(() => {
    document.title =
      "ERP & Business Management Software Development Company in Pune | HexxaTech";

    const description =
      "HexxaTech Softwares develops custom ERP and business management software in Pune for purchasing, sales, inventory, quotations, customers, vendors, reporting, approvals and business automation.";

    const setMeta = (name, content) => {
      let element = document.querySelector(`meta[name="${name}"]`);

      if (!element) {
        element = document.createElement("meta");
        element.setAttribute("name", name);
        document.head.appendChild(element);
      }

      element.setAttribute("content", content);
    };

    setMeta("description", description);

    let canonical = document.querySelector('link[rel="canonical"]');

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute("href", PAGE_URL);

    const setProperty = (property, content) => {
      let element = document.querySelector(
        `meta[property="${property}"]`
      );

      if (!element) {
        element = document.createElement("meta");
        element.setAttribute("property", property);
        document.head.appendChild(element);
      }

      element.setAttribute("content", content);
    };

    setProperty(
      "og:title",
      "ERP & Business Management Software Development | HexxaTech"
    );

    setProperty(
      "og:description",
      "Custom ERP and business management software for purchasing, sales, inventory, quotations, customers, vendors, reporting and business automation."
    );

    setProperty("og:type", "website");
    setProperty("og:url", PAGE_URL);
    setProperty("og:site_name", "HexxaTech Softwares");

    const existingSchema = document.getElementById(
      "hexxatech-erp-service-schema"
    );

    if (!existingSchema) {
      const script = document.createElement("script");

      script.id = "hexxatech-erp-service-schema";
      script.type = "application/ld+json";

      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Service",
        name: "ERP & Business Management Software Development",
        serviceType: "ERP Software Development",
        provider: {
          "@type": "Organization",
          name: "HexxaTech Softwares",
          url: SITE_URL,
        },
        url: PAGE_URL,
        description: description,
        areaServed: {
          "@type": "Country",
          name: "India",
        },
      });

      document.head.appendChild(script);
    }

    window.scrollTo(0, 0);
  }, []);

  const handleWhatsApp = () => {
    trackWhatsAppClick("erp_service_page");
  };

  const serviceNavigation = [
    { title: "Custom Software Development", to: "/services/custom-software-development" },
    { title: "ERP & Business Management", to: "/services/erp-business-management" },
    { title: "CRM & Lead Management", to: "/services/crm-lead-management" },
    { title: "Healthcare Software", to: "/services/healthcare-software" },
    { title: "Inventory Management", to: "/services/inventory-management" },
    { title: "Industrial Process Software", to: "/services/industrial-process-software" },
    { title: "License Management", to: "/services/license-management" },
    { title: "Mobile App Development", to: "/services/mobile-app-development" },
  ];

  const currentIndex = serviceNavigation.findIndex(
    (service) => service.to === "/services/erp-business-management"
  );
  const previousService =
    serviceNavigation[(currentIndex - 1 + serviceNavigation.length) % serviceNavigation.length];
  const nextService =
    serviceNavigation[(currentIndex + 1) % serviceNavigation.length];

  return (
    <div className="hexxa-erp-page">
      <style>{`
        * {
          box-sizing: border-box;
        }

        html {
          scroll-behavior: smooth;
        }

        .hexxa-erp-page {
          overflow: hidden;
          background: #ffffff;
          color: #0f172a;
          font-family: Inter, -apple-system, BlinkMacSystemFont,
            "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
        }

        .hexxa-erp-page .hexxa-container {
          width: min(1180px, calc(100% - 40px));
          margin: 0 auto;
        }

        /* =====================================
           HERO
        ===================================== */

        .erp-hero {
          position: relative;
          overflow: hidden;
          padding: 100px 0 90px;
          color: #ffffff;
          background:
            radial-gradient(
              circle at 85% 15%,
              rgba(59, 130, 246, 0.22),
              transparent 30%
            ),
            linear-gradient(
              135deg,
              #294a7d 0%,
              #355b91 55%,
              #294a7d 100%
            );
        }

        .erp-hero::before {
          content: "";
          position: absolute;
          width: 500px;
          height: 500px;
          border-radius: 50%;
          border: 1px solid rgba(255,255,255,.08);
          top: -300px;
          right: -100px;
        }

        .erp-hero-grid {
          position: relative;
          z-index: 1;
          display: grid;
          grid-template-columns: 1.05fr .95fr;
          gap: 70px;
          align-items: center;
        }

        .erp-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 7px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,.10);
          border: 1px solid rgba(255,255,255,.16);
          color: #dbeafe;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.2px;
          text-transform: uppercase;
          margin-bottom: 20px;
        }

        .erp-eyebrow-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #60a5fa;
        }

        .erp-hero h1 {
          margin: 0;
          max-width: 760px;
          font-size: clamp(40px, 5vw, 64px);
          line-height: 1.04;
          letter-spacing: -2.6px;
          font-weight: 800;
        }

        .erp-hero h1 span {
          color: #60a5fa;
        }

        .erp-hero-text {
          max-width: 680px;
          margin: 25px 0 30px;
          color: #dbeafe;
          font-size: 17px;
          line-height: 1.8;
        }

        .erp-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }

        .erp-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 48px;
          padding: 0 20px;
          border-radius: 10px;
          text-decoration: none;
          font-size: 14px;
          font-weight: 700;
          transition: .25s ease;
        }

        .erp-btn-primary {
          color: #ffffff;
          background: #2563eb;
          box-shadow: 0 12px 25px rgba(15,23,42,.20);
        }

        .erp-btn-primary:hover {
          transform: translateY(-2px);
          background: #1d4ed8;
        }

        .erp-btn-light {
          color: #ffffff;
          background: rgba(255,255,255,.08);
          border: 1px solid rgba(255,255,255,.22);
        }

        .erp-btn-light:hover {
          background: rgba(255,255,255,.15);
        }

        /* =====================================
           HERO VISUAL
        ===================================== */

        .erp-dashboard {
          position: relative;
          width: min(480px, 100%);
          margin: auto;
          padding: 16px;
          border-radius: 22px;
          background: rgba(255,255,255,.10);
          border: 1px solid rgba(255,255,255,.18);
          backdrop-filter: blur(10px);
          box-shadow: 0 30px 70px rgba(15,23,42,.20);
          transform: rotate(-2deg);
        }

        .erp-dashboard-top {
          display: flex;
          gap: 6px;
          padding-bottom: 14px;
        }

        .erp-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: rgba(255,255,255,.65);
        }

        .erp-dashboard-body {
          display: grid;
          grid-template-columns: 82px 1fr;
          gap: 12px;
        }

        .erp-sidebar {
          min-height: 280px;
          padding: 12px 8px;
          border-radius: 12px;
          background: rgba(255,255,255,.10);
        }

        .erp-side-line {
          height: 8px;
          margin-bottom: 13px;
          border-radius: 5px;
          background: rgba(255,255,255,.20);
        }

        .erp-side-line.active {
          background: #60a5fa;
        }

        .erp-main {
          display: grid;
          gap: 12px;
        }

        .erp-cards {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 10px;
        }

        .erp-card {
          min-height: 75px;
          padding: 13px;
          border-radius: 12px;
          background: rgba(255,255,255,.11);
          border: 1px solid rgba(255,255,255,.10);
        }

        .erp-card strong {
          display: block;
          margin-bottom: 5px;
          font-size: 18px;
        }

        .erp-card small {
          color: #cbd5e1;
          font-size: 9px;
        }

        .erp-table {
          padding: 15px;
          border-radius: 14px;
          background: rgba(255,255,255,.09);
          border: 1px solid rgba(255,255,255,.10);
        }

        .erp-table-row {
          display: grid;
          grid-template-columns: 1.3fr .8fr .7fr;
          gap: 8px;
          padding: 9px 0;
          border-bottom: 1px solid rgba(255,255,255,.08);
        }

        .erp-table-row:last-child {
          border-bottom: 0;
        }

        .erp-table-line {
          height: 7px;
          border-radius: 4px;
          background: rgba(255,255,255,.18);
        }

        .erp-table-line.short {
          width: 65%;
        }

        /* =====================================
           INTRO
        ===================================== */

        .erp-section {
          padding: 90px 0;
        }

        .erp-soft {
          background: #f8fafc;
        }

        .erp-label {
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }

        .erp-title {
          max-width: 800px;
          margin: 12px 0 0;
          color: #0f172a;
          font-size: clamp(32px, 4vw, 48px);
          line-height: 1.1;
          letter-spacing: -1.8px;
        }

        .erp-description {
          max-width: 760px;
          margin: 18px 0 0;
          color: #64748b;
          font-size: 16px;
          line-height: 1.8;
        }

        .erp-intro-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 70px;
          align-items: center;
        }

        .erp-intro-text p {
          color: #64748b;
          font-size: 15px;
          line-height: 1.85;
        }

        .erp-intro-box {
          padding: 35px;
          border-radius: 22px;
          border: 1px solid #e5e7eb;
          background: #ffffff;
          box-shadow: 0 20px 50px rgba(15,23,42,.06);
        }

        .erp-intro-box h3 {
          margin: 0 0 22px;
          font-size: 20px;
        }

        .erp-module-list {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
        }

        .erp-module {
          padding: 12px;
          border-radius: 10px;
          background: #f8fafc;
          color: #475569;
          font-size: 12px;
        }

        .erp-module span {
          margin-right: 7px;
          color: #2563eb;
          font-weight: 800;
        }

        /* =====================================
           FEATURES
        ===================================== */

        .erp-feature-header {
          max-width: 760px;
          margin: 0 auto 45px;
          text-align: center;
        }

        .erp-feature-header .erp-title {
          margin-left: auto;
          margin-right: auto;
        }

        .erp-feature-header .erp-description {
          margin-left: auto;
          margin-right: auto;
        }

        .erp-feature-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 16px;
        }

        .erp-feature {
          min-height: 220px;
          padding: 25px;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          background: #ffffff;
          transition: .3s ease;
        }

        .erp-feature:hover {
          transform: translateY(-5px);
          border-color: #bfdbfe;
          box-shadow: 0 18px 40px rgba(15,23,42,.07);
        }

        .erp-feature-icon {
          width: 42px;
          height: 42px;
          margin-bottom: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 11px;
          background: #eff6ff;
          color: #2563eb;
          font-size: 17px;
          font-weight: 800;
        }

        .erp-feature h3 {
          margin: 0 0 9px;
          font-size: 16px;
        }

        .erp-feature p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.7;
        }

        /* =====================================
           BENEFITS
        ===================================== */

        .erp-benefit-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 18px;
          margin-top: 40px;
        }

        .erp-benefit {
          padding: 26px;
          border-radius: 16px;
          background: #ffffff;
          border: 1px solid #e5e7eb;
        }

        .erp-benefit-number {
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1px;
        }

        .erp-benefit h3 {
          margin: 10px 0 8px;
          font-size: 17px;
        }

        .erp-benefit p {
          margin: 0;
          color: #64748b;
          font-size: 13px;
          line-height: 1.7;
        }

        /* =====================================
           PROCESS
        ===================================== */

        .erp-process-grid {
          display: grid;
          grid-template-columns: .85fr 1.15fr;
          gap: 70px;
          align-items: start;
        }

        .erp-process-list {
          display: grid;
          gap: 10px;
          margin-top: 35px;
        }

        .erp-process {
          padding: 21px;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          background: #ffffff;
        }

        .erp-process-number {
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 1px;
        }

        .erp-process h3 {
          margin: 7px 0;
          font-size: 15px;
        }

        .erp-process p {
          margin: 0;
          color: #64748b;
          font-size: 12px;
          line-height: 1.7;
        }

        .erp-process-visual {
          padding: 35px;
          border-radius: 24px;
          background: linear-gradient(145deg, #eff6ff, #ffffff);
          border: 1px solid #dbeafe;
        }

        .erp-flow {
          display: grid;
          gap: 10px;
          margin-top: 25px;
        }

        .erp-flow-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          background: #ffffff;
        }

        .erp-flow-number {
          width: 32px;
          height: 32px;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          background: #eff6ff;
          color: #2563eb;
          font-size: 11px;
          font-weight: 800;
        }

        .erp-flow-row strong {
          font-size: 12px;
        }

        /* =====================================
           INDUSTRIES
        ===================================== */

        .erp-industries {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 35px;
        }

        .erp-industry {
          padding: 11px 15px;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          background: #ffffff;
          color: #475569;
          font-size: 12px;
          font-weight: 600;
        }

        /* =====================================
           CASE STUDIES
        ===================================== */

        .erp-case-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
          margin-top: 40px;
        }

        .erp-case-card {
          display: flex;
          flex-direction: column;
          min-height: 280px;
          padding: 26px;
          border: 1px solid #e5e7eb;
          border-radius: 18px;
          background: #ffffff;
          color: inherit;
          text-decoration: none;
          transition: .3s ease;
        }

        .erp-case-card:hover {
          transform: translateY(-5px);
          border-color: #bfdbfe;
          box-shadow: 0 20px 45px rgba(15,23,42,.08);
        }

        .erp-case-category {
          align-self: flex-start;
          padding: 6px 9px;
          border-radius: 999px;
          background: #eff6ff;
          color: #2563eb;
          font-size: 9px;
          font-weight: 800;
          letter-spacing: .8px;
          text-transform: uppercase;
        }

        .erp-case-card h3 {
          margin: 20px 0 8px;
          font-size: 20px;
        }

        .erp-case-card p {
          margin: 0;
          color: #64748b;
          font-size: 13px;
          line-height: 1.7;
        }

        .erp-case-link {
          margin-top: auto;
          padding-top: 25px;
          color: #2563eb;
          font-size: 12px;
          font-weight: 800;
        }

        /* =====================================
           CTA
        ===================================== */

        .erp-cta {
          padding: 85px 0;
        }

        .erp-cta-box {
          position: relative;
          overflow: hidden;
          padding: 70px 30px;
          text-align: center;
          border-radius: 24px;
          color: #ffffff;
          background:
            radial-gradient(
              circle at 85% 15%,
              rgba(59,130,246,.30),
              transparent 30%
            ),
            linear-gradient(135deg, #0f172a, #172554);
        }

        .erp-cta-label {
          display: inline-block;
          padding: 7px 12px;
          border-radius: 999px;
          background: rgba(255,255,255,.08);
          border: 1px solid rgba(255,255,255,.10);
          color: #bfdbfe;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .erp-cta h2 {
          max-width: 720px;
          margin: 20px auto 12px;
          font-size: clamp(34px, 4vw, 50px);
          line-height: 1.1;
          letter-spacing: -1.5px;
        }

        .erp-cta p {
          max-width: 680px;
          margin: 0 auto 28px;
          color: #cbd5e1;
          font-size: 15px;
          line-height: 1.8;
        }

        .erp-cta .erp-btn-primary {
          color: #0f172a;
          background: #ffffff;
        }

        .erp-cta .erp-btn-primary:hover {
          background: #eff6ff;
        }

        /* =====================================
           MOBILE
        ===================================== */

        @media (max-width: 1000px) {
          .erp-hero-grid,
          .erp-intro-grid,
          .erp-process-grid {
            grid-template-columns: 1fr;
            gap: 45px;
          }

          .erp-feature-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .erp-benefit-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .erp-case-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .erp-dashboard {
            transform: none;
          }
        }

        @media (max-width: 700px) {
          .hexxa-erp-page .hexxa-container {
            width: min(100% - 28px, 1180px);
          }

          .erp-hero {
            padding: 75px 0 65px;
          }

          .erp-hero h1 {
            font-size: 42px;
            letter-spacing: -1.8px;
          }

          .erp-hero-text {
            font-size: 15px;
          }

          .erp-section {
            padding: 65px 0;
          }

          .erp-feature-grid,
          .erp-benefit-grid,
          .erp-case-grid {
            grid-template-columns: 1fr;
          }

          .erp-module-list {
            grid-template-columns: 1fr;
          }

          .erp-dashboard-body {
            grid-template-columns: 55px 1fr;
          }

          .erp-sidebar {
            min-height: 220px;
          }

          .erp-cards {
            grid-template-columns: 1fr;
          }

          .erp-intro-box,
          .erp-process-visual {
            padding: 22px;
          }

          .erp-cta {
            padding: 65px 0;
          }

          .erp-cta-box {
            padding: 50px 20px;
            border-radius: 18px;
          }
        }
      `}</style>

      {/* =========================================
          HERO
      ========================================= */}

      <section className="erp-hero">
        <div className="hexxa-container erp-hero-grid">
          <div>
            <div className="erp-eyebrow">
              <span className="erp-eyebrow-dot"></span>
              ERP & Business Management
            </div>

            <h1>
              One platform for
              <br />
              <span>your business operations.</span>
            </h1>

            <p className="erp-hero-text">
              HexxaTech Softwares develops custom ERP and business management
              software that connects purchasing, sales, inventory, customers,
              vendors, quotations, reporting and operational workflows in one
              centralized platform.
            </p>

            <div className="erp-buttons">
              <a
                href="https://wa.me/919226591664?text=Hello%20HexxaTech%2C%20I%20would%20like%20to%20discuss%20an%20ERP%20or%20business%20management%20software%20project."
                target="_blank"
                rel="noopener noreferrer"
                className="erp-btn erp-btn-primary"
                onClick={handleWhatsApp}
              >
                Discuss Your ERP Project →
              </a>

              <Link
                to="/case-studies"
                className="erp-btn erp-btn-light"
                onClick={() =>
                  trackEventCustom("erp_case_studies_click", {
                    location: "erp_hero",
                  })
                }
              >
                View Our Work →
              </Link>
            </div>
          </div>

          <div>
            <div className="erp-dashboard">
              <div className="erp-dashboard-top">
                <span className="erp-dot"></span>
                <span className="erp-dot"></span>
                <span className="erp-dot"></span>
              </div>

              <div className="erp-dashboard-body">
                <div className="erp-sidebar">
                  <div className="erp-side-line active"></div>
                  <div className="erp-side-line"></div>
                  <div className="erp-side-line"></div>
                  <div className="erp-side-line"></div>
                  <div className="erp-side-line"></div>
                  <div className="erp-side-line"></div>
                </div>

                <div className="erp-main">
                  <div className="erp-cards">
                    <div className="erp-card">
                      <strong>Sales</strong>
                      <small>Transactions</small>
                    </div>

                    <div className="erp-card">
                      <strong>Stock</strong>
                      <small>Inventory</small>
                    </div>

                    <div className="erp-card">
                      <strong>ERP</strong>
                      <small>Operations</small>
                    </div>
                  </div>

                  <div className="erp-table">
                    {[1, 2, 3, 4, 5].map((item) => (
                      <div className="erp-table-row" key={item}>
                        <div className="erp-table-line"></div>
                        <div className="erp-table-line short"></div>
                        <div className="erp-table-line short"></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          INTRO
      ========================================= */}

      <section className="erp-section">
        <div className="hexxa-container erp-intro-grid">
          <div className="erp-intro-text">
            <span className="erp-label">
              Custom ERP Development
            </span>

            <h2 className="erp-title">
              ERP software designed around your business process.
            </h2>

            <p>
              Every business has different processes, approval structures,
              transaction flows and reporting requirements. A generic ERP may
              not always fit the way your organization operates.
            </p>

            <p>
              HexxaTech develops custom ERP and business management systems
              around your actual workflows. We connect departments,
              transactions and business information into a single platform
              designed for practical day-to-day use.
            </p>

            <p>
              From small business management systems to complex
              multi-module ERP platforms, we build software that can grow
              alongside your organization.
            </p>
          </div>

          <div className="erp-intro-box">
            <h3>Common ERP Modules</h3>

            <div className="erp-module-list">
              {modules.map((module) => (
                <div className="erp-module" key={module}>
                  <span>✓</span>
                  {module}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* =========================================
          FEATURES
      ========================================= */}

      <section className="erp-section erp-soft">
        <div className="hexxa-container">
          <div className="erp-feature-header">
            <span className="erp-label">
              ERP Features
            </span>

            <h2 className="erp-title">
              Everything your teams need to manage daily operations.
            </h2>

            <p className="erp-description">
              Build a connected business platform where transactions,
              information and workflows move together instead of remaining
              scattered across spreadsheets and disconnected applications.
            </p>
          </div>

          <div className="erp-feature-grid">
            {features.map((feature, index) => (
              <article
                className="erp-feature"
                key={feature.title}
                onClick={() =>
                  trackEventCustom("erp_feature_interest", {
                    feature: feature.title,
                  })
                }
              >
                <div className="erp-feature-icon">
                  {String(index + 1).padStart(2, "0")}
                </div>

                <h3>{feature.title}</h3>

                <p>{feature.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          BENEFITS
      ========================================= */}

      <section className="erp-section">
        <div className="hexxa-container">
          <span className="erp-label">
            Why Custom ERP
          </span>

          <h2 className="erp-title">
            More control over the way your business operates.
          </h2>

          <p className="erp-description">
            A properly designed ERP system can connect business processes,
            improve information flow and provide management with better
            operational visibility.
          </p>

          <div className="erp-benefit-grid">
            {benefits.map((benefit, index) => (
              <div className="erp-benefit" key={benefit.title}>
                <div className="erp-benefit-number">
                  {String(index + 1).padStart(2, "0")}
                </div>

                <h3>{benefit.title}</h3>

                <p>{benefit.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          PROCESS
      ========================================= */}

      <section className="erp-section erp-soft">
        <div className="hexxa-container erp-process-grid">
          <div>
            <span className="erp-label">
              ERP Development Process
            </span>

            <h2 className="erp-title">
              From business processes to a connected ERP platform.
            </h2>

            <p className="erp-description">
              We first understand how your organization works and then design
              the ERP system around those requirements.
            </p>

            <div className="erp-process-list">
              {processSteps.map((step) => (
                <div className="erp-process" key={step.number}>
                  <div className="erp-process-number">
                    {step.number}
                  </div>

                  <h3>{step.title}</h3>

                  <p>{step.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="erp-process-visual">
            <span className="erp-label">
              ERP Workflow
            </span>

            <h3
              style={{
                margin: "12px 0 0",
                fontSize: "23px",
              }}
            >
              Connected business operations.
            </h3>

            <div className="erp-flow">
              {[
                "Customer / Vendor",
                "Quotation / Purchase",
                "Sales / Orders",
                "Inventory Transactions",
                "Billing & Payments",
                "Reports & Management",
              ].map((item, index) => (
                <div className="erp-flow-row" key={item}>
                  <div className="erp-flow-number">
                    {String(index + 1).padStart(2, "0")}
                  </div>

                  <strong>{item}</strong>
                </div>
              ))}
            </div>

            <p
              style={{
                margin: "22px 0 0",
                color: "#64748b",
                fontSize: "13px",
                lineHeight: 1.7,
              }}
            >
              Instead of treating each department as a separate system,
              custom ERP development connects business activities into one
              consistent workflow.
            </p>
          </div>
        </div>
      </section>

      {/* =========================================
          INDUSTRIES
      ========================================= */}

      <section className="erp-section">
        <div className="hexxa-container">
          <span className="erp-label">
            Industries
          </span>

          <h2 className="erp-title">
            ERP software for different business environments.
          </h2>

          <p className="erp-description">
            Our custom ERP approach can be adapted to different operational
            models, transaction flows and business requirements.
          </p>

          <div className="erp-industries">
            {industries.map((industry) => (
              <span className="erp-industry" key={industry}>
                {industry}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* =========================================
          CASE STUDIES
      ========================================= */}

      <section className="erp-section erp-soft">
        <div className="hexxa-container">
          <span className="erp-label">
            Selected Work
          </span>

          <h2 className="erp-title">
            Business software developed for real workflows.
          </h2>

          <p className="erp-description">
            Explore selected HexxaTech projects involving business
            management, inventory, industrial processes and operational
            automation.
          </p>

          <div className="erp-case-grid">
            {caseStudies.map((study) => (
              <Link
                to={study.link}
                className="erp-case-card"
                key={study.title}
                onClick={() =>
                  trackEventCustom("erp_case_study_click", {
                    case_study: study.title,
                    category: study.category,
                  })
                }
              >
                <span className="erp-case-category">
                  {study.category}
                </span>

                <h3>{study.title}</h3>

                <p>{study.description}</p>

                <div className="erp-case-link">
                  View case study →
                </div>
              </Link>
            ))}
          </div>

          <div
            style={{
              textAlign: "center",
              marginTop: "35px",
            }}
          >
            <Link
              to="/case-studies"
              className="erp-btn erp-btn-primary"
              style={{
                color: "#ffffff",
              }}
              onClick={() =>
                trackEventCustom("erp_all_case_studies_click", {
                  location: "erp_service_page",
                })
              }
            >
              Explore All Case Studies →
            </Link>
          </div>
        </div>
      </section>

      {/* =========================================
          SEO CONTENT
      ========================================= */}

      <section className="erp-section">
        <div
          className="hexxa-container"
          style={{
            maxWidth: "900px",
          }}
        >
          <span className="erp-label">
            ERP Software Development in Pune
          </span>

          <h2
            className="erp-title"
            style={{
              fontSize: "30px",
            }}
          >
            Custom ERP & Business Management Software Company in Pune
          </h2>

          <p
            style={{
              color: "#64748b",
              fontSize: "14px",
              lineHeight: 1.85,
            }}
          >
            HexxaTech Softwares develops custom ERP and business management
            software for businesses in Pune and across India. Our ERP
            solutions can include purchase management, sales management,
            inventory management, quotations, customer management, vendor
            management, order processing, invoicing, payment tracking,
            dashboards and business reporting.
          </p>

          <p
            style={{
              color: "#64748b",
              fontSize: "14px",
              lineHeight: 1.85,
            }}
          >
            Our approach focuses on building ERP software around the actual
            business workflow instead of forcing an organization to change
            its processes to fit a generic application. Custom workflows,
            approval processes, database structures, reports and business
            rules can be designed according to the organization's
            requirements.
          </p>

          <p
            style={{
              color: "#64748b",
              fontSize: "14px",
              lineHeight: 1.85,
            }}
          >
            Whether you need a small business management application or a
            larger multi-module ERP platform, HexxaTech can help design and
            develop software that centralizes information, improves
            operational visibility and supports long-term business growth.
          </p>
        </div>
      </section>

      {/* =========================================
          SERVICE NAVIGATION
      ========================================= */}

      <section
        style={{
          padding: "0 0 70px",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            width: "min(100% - 32px, 1100px)",
            margin: "0 auto",
            maxWidth: "1100px",
          }}
        >
          <span className="erp-label">
            Explore Our Services
          </span>

          <h2
            className="erp-title"
            style={{ marginTop: "12px", fontSize: "28px" }}
          >
            Explore our other software development services.
          </h2>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))",
              gap: "12px",
              marginTop: "22px",
            }}
          >
            {serviceNavigation.map((service) => {
              const isCurrent = service.to === PAGE_URL;

              return (
                <Link
                  key={service.to}
                  to={service.to}
                  style={{
                    display: "block",
                    padding: "16px 18px",
                    borderRadius: "12px",
                    textDecoration: "none",
                    border: isCurrent ? "1px solid #2563eb" : "1px solid #e5e7eb",
                    background: isCurrent ? "#eff6ff" : "#f8fafc",
                    color: isCurrent ? "#1d4ed8" : "#334155",
                    fontWeight: isCurrent ? 700 : 600,
                  }}
                >
                  {service.title}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section
        style={{
          padding: "0 0 70px",
          background: "#ffffff",
        }}
      >
        <div
          style={{
            width: "min(100% - 32px, 1100px)",
            margin: "0 auto",
            maxWidth: "1100px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "12px",
              flexWrap: "wrap",
              borderTop: "1px solid #e5e7eb",
              paddingTop: "20px",
            }}
          >
            <Link
              to={previousService.to}
              style={{ color: "#2563eb", textDecoration: "none", fontWeight: 600 }}
            >
              ← Previous: {previousService.title}
            </Link>

            <Link
              to={nextService.to}
              style={{ color: "#2563eb", textDecoration: "none", fontWeight: 600 }}
            >
              Next: {nextService.title} →
            </Link>
          </div>
        </div>
      </section>

      {/* =========================================
          CTA
      ========================================= */}

      <section className="erp-cta">
        <div className="hexxa-container">
          <div className="erp-cta-box">
            <span className="erp-cta-label">
              Build Your ERP
            </span>

            <h2>
              Need an ERP system designed around your business?
            </h2>

            <p>
              Tell us about your current processes, departments, reports and
              operational challenges. We can help plan a custom ERP solution
              around the way your business actually works.
            </p>

            <a
              href="https://wa.me/919226591664?text=Hello%20HexxaTech%2C%20I%20would%20like%20to%20discuss%20an%20ERP%20or%20business%20management%20software%20project."
              target="_blank"
              rel="noopener noreferrer"
              className="erp-btn erp-btn-primary"
              onClick={handleWhatsApp}
            >
              Discuss Your ERP Project →
            </a>
          </div>
        </div>
      </section>

      {/* =========================================
          INTERNAL LINKS
      ========================================= */}

      <section
        style={{
          padding: "0 0 70px",
          background: "#ffffff",
        }}
      >
        <div
          className="hexxa-container"
          style={{
            maxWidth: "900px",
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
            justifyContent: "center",
          }}
        >
          <Link
            to="/services/custom-software-development"
            className="erp-btn erp-btn-light"
            style={{
              color: "#2563eb",
              border: "1px solid #bfdbfe",
              background: "#eff6ff",
            }}
          >
            Custom Software Development
          </Link>

          <Link
            to="/services/crm-lead-management"
            className="erp-btn erp-btn-light"
            style={{
              color: "#2563eb",
              border: "1px solid #bfdbfe",
              background: "#eff6ff",
            }}
          >
            CRM & Lead Management
          </Link>

          <Link
            to="/services/inventory-management"
            className="erp-btn erp-btn-light"
            style={{
              color: "#2563eb",
              border: "1px solid #bfdbfe",
              background: "#eff6ff",
            }}
          >
            Inventory Management
          </Link>

          <Link
            to="/services"
            className="erp-btn erp-btn-light"
            style={{
              color: "#2563eb",
              border: "1px solid #bfdbfe",
              background: "#eff6ff",
            }}
          >
            All Services
          </Link>
        </div>
      </section>
    </div>
  );
}